/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { globalProductSearch } from '@/lib/actions/globalSearchAction'
import Spinner from '@/components/Spinner'
import Placeholder from '@/components/PlaceHolder'
import Icons from '@/components/icons'
import { getSEOFriendlyURL } from '@/lib/utilities/Utils'
import HorizontalScrollWithArrows from '@/routes/Common/horizontal-scroll'
import ProductCard from '@/components/common/product-card'
import { type GetServerSideProps } from 'next'
import globalSearchApis from '@/apis/global-search'

interface CategoryLandingProps {
  q: string
  categoryId: string
  subCategoryId: string | null
}

export const getServerSideProps: GetServerSideProps<CategoryLandingProps> = async (context) => {
  const slug = context.params?.slug as string[] | undefined

  const { q = '' } = context.query as { q?: string }

  const pCat = slug?.[0] ?? ''
  const sCat = slug?.[1] ?? ''

  const finalCategoryId = pCat !== '' ? pCat.replace(/-/g, '_') : ''
  const subCategoryId = sCat !== '' ? sCat.replace(/-/g, '_') : null

  return {
    props: {
      q,
      categoryId: finalCategoryId,
      subCategoryId
    }
  }
}

interface ProductData {
  data: any[]
  totalCount: number
  count: number
  next?: boolean
}

const CategoryLanding = ({ q, categoryId, subCategoryId }: CategoryLandingProps): JSX.Element => {
  const router = useRouter()

  const [productsData, setProductsData] = useState<ProductData | null>(null)
  const [isLoading, setLoading] = useState(true)
  const [isError, setError] = useState(false)
  const [offset, setOffset] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState<any>(null)
  const [selectedSubCategory, setSelectedSubCat] = useState<any>(null)
  const [categories, setCategories] = useState<any>([])
  const [filters, setFilters] = useState({
    searchText: q,
    categoryId,
    subCategoryId
  })
  const [state, setState] = useState<any>({
    page: 1,
    lazyLoader: false
  })

  const showWIP = router.query.slug === undefined

  const {
    page,
    lazyLoader
  } = state

  useEffect(() => {
    setFilters({
      searchText: q,
      categoryId,
      subCategoryId
    })
  }, [q, categoryId, subCategoryId])

  const loadCategories = async (): Promise<void> => {
    try {
      const res = await globalSearchApis.getGlobalCategories()
      if (res?.status === 200) {
        setCategories(res.data?.data ?? [])
      }
    } catch (err) {
    }
  }

  const loadOnlineProducts = (resetOffset = false): void => {
    if (categories.length === 0) return

    setLoading(true)
    setError(false)

    const catMeta = categories.find(
      (i: any) => i.parentCategory?.[0]?.code === filters.categoryId
    )
    const subCat = catMeta?.secondaryCategory?.find(
      (i: any) => i.code === filters.subCategoryId
    )

    if (catMeta == null) {
      setLoading(false)
      return
    }

    globalProductSearch({
      searchText: filters.searchText,
      productCategories: subCat?.id ?? catMeta.id,
      brandId: '',
      productId: '',
      limit: 5,
      page: resetOffset ? 1 : offset
    })
      .then((response: any) => {
        setSelectedCategory(catMeta)
        setSelectedSubCat(subCat)

        const data = response.data
        if (resetOffset || productsData === null) {
          setProductsData(data)
          setOffset(2)
        } else {
          setProductsData({
            data: [...productsData.data, ...data.data],
            totalCount: data.totalCount,
            count: data.count
          })
          setOffset(offset + 1)
        }
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleLazyLoad = (e: any): void => {
    const {
      scrollTop, offsetHeight, scrollHeight, id
    } = e.target
    if (id !== 'category-landing-section') {
      return
    }
    if (!isLoading && productsData !== null && (productsData?.count < productsData?.totalCount) &&
        (scrollHeight - offsetHeight - scrollTop < 10 ||
          offsetHeight + scrollTop === scrollHeight
        )
    ) {
      setState({
        ...state,
        page: page + 1,
        lazyLoader: true
      })
      setTimeout(() => {
        loadOnlineProducts()
      }, 200)
    }
  }

  useEffect(() => {
    void loadCategories()
  }, [])

  useEffect(() => {
    if (categories.length > 0) {
      loadOnlineProducts(true)
    }
  }, [categories, filters])

  return (
    <div className="w-full container mx-auto bg-white">
      {(showWIP)
        ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          <h2 className="text-2xl font-semibold text-gray-600">
            Work in Progress 🚧
          </h2>
        </div>
          )
        : (
        <div
          id="category-landing-section"
          className="w-full flex flex-col"
          onScroll={productsData !== null ? undefined : handleLazyLoad}
        >
          {/* Title + Breadcrumbs */}
          {selectedCategory !== null && (
            <div className="px-4 py-6 border-b">
              <h1 className="text-2xl font-bold">
                {selectedCategory?.parentCategory?.[0]?.name}
              </h1>
              <div className="text-gray-500 text-sm flex gap-2 mt-2">
                <Link href="/">Home</Link> / <Link href="/pc">All</Link> /{' '}
                <span>{selectedCategory?.parentCategory?.[0]?.name}</span>
                {filters.subCategoryId !== '' && (<span>{selectedSubCategory?.name}</span>)}
              </div>
            </div>
          )}

          {/* Products */}
          {!isLoading && productsData !== null && productsData?.data?.length > 0
            ? (
                productsData?.data.map((stores: any) => (
                  <div key={stores.id} className="border-b py-6">
                    <div className="px-4 flex justify-between">
                      <div>
                        <span className="font-bold">
                          {stores?.name}
                        </span>
                        <div className="flex items-center text-[12px] font-bold">
                          <span>
                            <Icons name="star3" />
                          </span>
                          <span className="mx-0.5">
                            {(stores?.rating > 0 && stores?.rating?.toFixed(2)) ?? '--'}
                          </span>
                          <span className="mt-[-5px] mx-1">.</span>
                          <span>
                            <Icons name="bike" />
                          </span>
                          <span className="mx-0.5">
                            {stores?.averageDeliveryTimeString ?? '--'}
                          </span>
                          <span className="mt-[-5px] mx-1">.</span>
                          <span>
                            {(stores.calculatedDistance / 1000).toFixed(2) ?? '--'} Km
                          </span>
                          {Boolean(stores?.isFreeSampleStore) && (
                            <div className="flex">
                              <span className="mt-[2px] mx-1.5">.</span>
                              <Icons name="free-sample" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <button
                          className="font-bold text-pink-600 flex justify-center gap-1"
                          onClick={() => {
                            router
                              .push(
                                `/mumbai/andheri/grocery/${getSEOFriendlyURL(
                                  stores?.name
                                )}-${stores?.id}`.toLocaleLowerCase()
                              )
                              .catch(() => {})
                          }}
                        >
                          Visit Store
                          <Icons name="circle-arrow" fill="#B8238E" stroke="#ffffff" width="1.3rem" />
                        </button>
                      </div>
                    </div>

                    <div className="px-4 flex overflow-x-scroll scrollbar-none gap-4 mt-4">
                      <HorizontalScrollWithArrows>
                        {stores.productsWithMeasurements.map((product: any) => (
                          <ProductCard
                            key={product?.retailerProductId}
                            item={product}
                          />
                        ))}

                        {stores.comboOffers.map((product: any) => (
                          <ProductCard
                            key={product?.retailerProductId}
                            item={product}
                            isOfferProduct
                          />
                        ))}

                        {stores.relatedProducts.map((product: any) => (
                          <ProductCard
                            key={product?.retailerProductId}
                            item={product}
                          />
                        ))}
                      </HorizontalScrollWithArrows>
                    </div>
                  </div>
                ))
              )
            : !isLoading && !isError && (
              <div className="flex justify-center py-6">
                <Placeholder
                  handleRetry={() => { loadOnlineProducts() }}
                  imageSrc="/images/no-results.png"
                  heading="No products found"
                />
              </div>
              )}

          {/* Loader */}
          {isLoading && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}
          {/* Error */}
          {isError && productsData?.data?.length === 0 && (
            <div className="flex justify-center py-6">
              <Placeholder
                handleRetry={() => { loadOnlineProducts() }}
                imageSrc="/images/error-placeholder.png"
                heading="Oh no! Something went wrong"
              />
            </div>
          )}
        </div>
          )}
    </div>
  )
}

export default CategoryLanding
